import type { JwtPayload } from 'jwt-decode';
import type { Permission } from '../types/roles';

export interface RecentItem {
  type: string;
  lastAccessedTime: string;
  id: string;
}

export interface RecentWorkflow {
  lastAccessedTime: string;
  workflowId: string;
}

export interface UserSettings {
  defaultDashboard?: string;
  preferredLocale: string;
  defaultNumberOfRecords: number;
  timeFormat: string;
  cookieConsent?: { necessary: boolean; statistics: boolean; }
  skillLevel?: number;
  department?: string | null;
}

export interface UpdateUserSettingsRequest {
  tenantId: string;
  userId: string;
  userSettings: UserSettings;
}

export type Tags = Record<string, string | string[]>

export interface UserInfo {
  favoriteWorkflows: string[];
  lastName: string;
  group: string;
  entity: string;
  created: string;
  firstName: string;
  lastLogin: string;
  userSettings: UserSettings;
  active: boolean;
  modified: string;
  email: string;
  userId: string;
  tenantId: string;
  modifiedBy: string;
  recentWorkflows: RecentWorkflow[];
  tags: Tags;
  isTenantAdmin: boolean;
  isTenantUser: boolean;
  isVinceLiveAdmin: boolean;
  isVinceLiveUser: boolean;
  tier: string;
  groups: string[];
  isMasterTenant: boolean;
  nickname: string;
  username: string;
  issuer: string;
  tenantType: string;
}

export interface UserPermission {
  permissions: Permission[];
  tenantPermissions: Permission[];
}

export interface UserPool {
  companyName: string;
  tenantAlias: string;
  ssoProviders: string[];
  userPoolId: string;
  ssoEnabled: boolean;
  region: string;
  tenantId: string;
  userPoolClientId: string;
  enforceSso: boolean;
}

export interface IdentityProvider {
  clientId: string;
  clientSecret: string;
  issuerUrl: string;
  providerName: string;
}
export interface VrnPermission {
    vrn: string;
    action: string;
}
export interface TenantInfo {
  active: boolean;
  address: string;
  billingEmail: string;
  companyEmail: string;
  companyName: string;
  companyPhone: string;
  created: string;
  createdBy: string;
  entity: string;
  mfaEnabled: boolean;
  modified: string;
  modifiedBy: string;
  permissions: VrnPermission[];
  region: string;
  enforceSso: boolean;
  ssoEnabled: boolean;
  ssoProviders: string[];
  tenantId: string;
  tenantAlias: string;
  tenantName: string;
  tenantType: string;
  tier: string;
  userPoolClientId: string;
  userPoolId: string;
  identityProviders: IdentityProvider[];
  settings?: {
    industry?: string | null;
    dedicatedIp?: {
      enabled?: boolean;
      ipAllocation?: {
        ipv4Addresses?: string[];
      };
    };
  };
}

export type VinceUserJWTPayload = JwtPayload & {
  'cognito:groups': string[];
  nickname: string;
}


export enum ExternalProvider {
  MICROSOFT = 'MICROSOFT'
}


export interface ExternalProviderUserInfo {
  id: string;
  name: string;
  email: string;
}

export interface Authentication {
  provider: ExternalProvider;
  expireOn?: Date;
  accessToken: string;
  userInfo : ExternalProviderUserInfo;
}

export interface ExternalProviderAdapter {
  /**
   * Determines if current application is configured properly for external provider
   */
  available?: () => Promise<boolean>;

  /**
   * Create context for further authorization requests
   */
  initialize: () => Promise<void>;

  /**
   * Authorize user with external provider
   */
  authenticate: () => Promise<Authentication>;
}
